import React, { createContext, useContext, useState, useEffect } from 'react';
import authService from '../services/authService';

export interface User {
  id: string;
  email: string;
  name: string;
  username?: string;
  roles: string[];
  role?: string; // Adding role for backward compatibility
}

interface AuthContextType {
  user: User | null;
  setUser: (user: User | null) => void;
  loading: boolean; // Changed from isLoading to loading
  isAuthenticated: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
}

export const AuthContext = createContext<AuthContextType>({
  user: null,
  setUser: () => {},
  loading: true, // Changed from isLoading to loading
  isAuthenticated: false,
  error: null,
  login: async () => {},
  register: async () => {},
  logout: async () => {},
  clearError: () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true); // Changed from isLoading to loading
  const [error, setError] = useState<string | null>(null);
  const isAuthenticated = !!user;

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const response = await authService.getCurrentUser();
          setUser(response.user);
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        localStorage.removeItem('token');
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await authService.login(email, password);
      
      // Get the token from the response
      const token = response?.tokens?.accessToken || 
                   response?.token || 
                   response?.data?.token ||
                   response?.data?.tokens?.accessToken;
      
      if (token) {
        localStorage.setItem('token', token);
        // After setting the token, get the user data
        const userResponse = await authService.getCurrentUser();
        setUser(userResponse.user);
      } else {
        throw new Error('No token received from server');
      }
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err?.response?.data?.message || err?.message || 'Login failed');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const register = async (name: string, email: string, password: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await authService.register(name, email, password);
      
      // Get the token from the response
      const token = response?.tokens?.accessToken || 
                   response?.token || 
                   response?.data?.token ||
                   response?.data?.tokens?.accessToken;
      
      if (token) {
        localStorage.setItem('token', token);
        // After setting the token, get the user data
        const userResponse = await authService.getCurrentUser();
        setUser(userResponse.user);
      } else {
        throw new Error('No token received from server');
      }
    } catch (err: any) {
      console.error('Registration error:', err);
      setError(err?.response?.data?.message || err?.message || 'Registration failed');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      localStorage.removeItem('token');
      setUser(null);
    }
  };

  const clearError = () => {
    setError(null);
  };

  return (
    <AuthContext.Provider 
      value={{ 
        user, 
        setUser, 
        loading, 
        isAuthenticated, 
        error,
        login,
        register,
        logout,
        clearError
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}; 